/**
 * Achievements Page
 * Shows all achievements with lock/unlock status and progress
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACHIEVEMENTS, Achievement, CATEGORY_NAMES, RARITY_COLORS, AchievementCategory, AchievementRarity } from '../data/achievements';
import { getAchievementProgress } from '../utils/achievements';
import { supabase, isSupabaseConfigured } from '../services/supabaseClient';
import { getUserAchievementsLocal } from '../utils/indexedDB';
import { ReadingStats } from '../types';
import { usePageMeta } from '../hooks/usePageMeta';

interface AchievementsPageProps {
  stats: ReadingStats;
  userTier?: string;
}

const AchievementsPage: React.FC<AchievementsPageProps> = ({ stats, userTier }) => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Reading Achievements & Gamification | Flash-Read',
    description: 'Track your reading achievements, earn XP, unlock badges across 10 categories. Reading gamification with streaks, daily challenges, and level progression.',
  });

  const [filterCategory, setFilterCategory] = useState<AchievementCategory | 'all'>('all');
  const [filterRarity, setFilterRarity] = useState<AchievementRarity | 'all'>('all');
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Check if user can earn achievements (Reader or Ultra tier)
  const canEarnAchievements = userTier === 'reader' || userTier === 'ultra';

  // Load user's unlocked achievements
  useEffect(() => {
    const loadAchievements = async () => {
      if (!isSupabaseConfigured) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          if (userTier === 'ultra') {
            // Ultra: read from Supabase (cloud sync)
            const { getUserAchievements } = await import('../services/supabaseClient');
            const achievements = await getUserAchievements(user.id);
            setUnlockedIds(new Set(achievements.map((a: { achievement_id: string }) => a.achievement_id)));
          } else {
            // Non-ultra: read from IndexedDB (local save)
            const achievements = await getUserAchievementsLocal(user.id);
            setUnlockedIds(new Set(achievements.map(a => a.achievementId)));
          }
        }
      } catch (error) {
        console.error('Error loading achievements:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAchievements();
  }, []);

  // Filter achievements
  const filteredAchievements = ACHIEVEMENTS.filter(achievement => {
    if (filterCategory !== 'all' && achievement.category !== filterCategory) return false;
    if (filterRarity !== 'all' && achievement.rarity !== filterRarity) return false;
    return true;
  });

  // Group by category for stats
  const categoryStats = ACHIEVEMENTS.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = { total: 0, unlocked: 0 };
    }
    acc[achievement.category].total++;
    if (unlockedIds.has(achievement.id)) {
      acc[achievement.category].unlocked++;
    }
    return acc;
  }, {} as Record<string, { total: number; unlocked: number }>);

  const totalAchievements = ACHIEVEMENTS.length;
  const totalUnlocked = unlockedIds.size;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-32 pb-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">Level Up</span> Your Reading Life
          </h1>
           <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
             <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
              Track your <span className="keyword-highlight">reading achievements</span> across 10 categories.<br />
              Our <span className="keyword-highlight">reading gamification</span> system rewards every milestone.<br />
              Build <span className="keyword-highlight">reading streaks</span>, earn <span className="keyword-highlight">XP and levels</span>, and unlock <span className="keyword-highlight">achievement badges</span>.<br />
              The more you read, the more you earn in <span className="keyword-highlight">reading rewards</span>.
            </p>
          </div>
        </div>

        {/* Upgrade Prompt for Guest/Starter users */}
        {!canEarnAchievements && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 mb-8 text-white">
            <div className="flex items-start gap-4">
              <div className="text-4xl">🔓</div>
              <div>
                <h2 className="text-xl font-bold mb-2">Unlock Your Achievements!</h2>
                <p className="text-blue-100 mb-4">
                  Upgrade to Reader or Ultra tier to start earning achievements and tracking your progress!
                </p>
                <button
                  onClick={() => navigate('/signup#pricing')}
                  className="bg-white text-blue-600 px-6 py-2 rounded-lg font-bold hover:bg-blue-50 transition-colors"
                >
                  View Plans →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md">
            <div className="text-3xl font-bold text-amber-500">{totalUnlocked}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Unlocked</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md">
            <div className="text-3xl font-bold text-slate-600 dark:text-slate-300">{totalAchievements}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Total</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md">
            <div className="text-3xl font-bold text-green-500">
              {totalAchievements > 0 ? Math.round((totalUnlocked / totalAchievements) * 100) : 0}%
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Complete</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md">
            <div className="text-3xl font-bold text-purple-500">
              {ACHIEVEMENTS.filter(a => unlockedIds.has(a.id)).reduce((sum, a) => sum + a.xpReward, 0)}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">XP Earned</div>
          </div>
        </div>

        {/* Category Progress */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-md mb-8">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Progress by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(CATEGORY_NAMES).map(([key, name]) => {
              const category = key as AchievementCategory;
              const catStats = categoryStats[category] || { total: 0, unlocked: 0 };
              const progress = catStats.total > 0 ? (catStats.unlocked / catStats.total) * 100 : 0;
              
              return (
                <div key={key} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-2xl">
                    {getCategoryIcon(category)}
                  </div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">{name}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {catStats.unlocked}/{catStats.total}
                  </div>
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full mt-1">
                    <div 
                      className="h-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as AchievementCategory | 'all')}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Rarity
            </label>
            <select
              value={filterRarity}
              onChange={(e) => setFilterRarity(e.target.value as AchievementRarity | 'all')}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
            >
              <option value="all">All Rarities</option>
              <option value="common">Common</option>
              <option value="uncommon">Uncommon</option>
              <option value="rare">Rare</option>
              <option value="epic">Epic</option>
              <option value="legendary">Legendary</option>
              <option value="mythic">Mythic</option>
            </select>
          </div>
        </div>

        {/* Achievements Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-slate-600 dark:text-slate-400 mt-4">Loading achievements...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAchievements.map(achievement => {
              const isUnlocked = unlockedIds.has(achievement.id);
              const progress = isUnlocked ? 100 : getAchievementProgress(achievement, stats, null);
              const rarityColor = RARITY_COLORS[achievement.rarity];

              return (
                <div
                  key={achievement.id}
                  className={`
                    relative bg-white dark:bg-slate-800 rounded-xl p-4 shadow-md
                    border-2 transition-all hover:shadow-lg
                    ${isUnlocked 
                      ? rarityColor.split(' ')[1] || 'border-amber-400'
                      : 'border-slate-200 dark:border-slate-700 opacity-75'
                    }
                  `}
                >
                  {/* Lock Overlay */}
                  {!isUnlocked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 rounded-xl">
                      <span className="text-4xl">🔒</span>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`
                      text-4xl p-3 rounded-lg bg-slate-100 dark:bg-slate-700
                      border-2 ${rarityColor.split(' ')[1] || 'border-slate-300'}
                    `}>
                      {achievement.icon}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        {achievement.name}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                        {achievement.description}
                      </p>
                      
                      {/* Meta Info */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-amber-500 font-bold text-sm">
                          +{achievement.xpReward} XP
                        </span>
                        <span className={`
                          text-xs px-2 py-0.5 rounded-full
                          ${getRarityBgColor(achievement.rarity)}
                        `}>
                          {achievement.rarity}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {CATEGORY_NAMES[achievement.category]}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      {!isUnlocked && progress > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Progress</span>
                            <span>{Math.round(progress)}%</span>
                          </div>
                          <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full">
                            <div 
                              className="h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Unlocked Badge */}
                      {isUnlocked && (
                        <div className="mt-2 flex items-center gap-1 text-green-500 text-sm">
                          <span>✓</span> Unlocked
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredAchievements.length === 0 && !isLoading && (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            No achievements match your filters
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions
function getCategoryIcon(category: AchievementCategory): string {
  const icons: Record<AchievementCategory, string> = {
    speed: '⚡',
    volume: '📚',
    streak: '🔥',
    challenge: '🎯',
    session: '📖',
    variety: '🌈',
    time: '⏰',
    social: '👥',
    milestone: '🏆',
    secret: '🔮',
  };
  return icons[category] || '🎖️';
}

function getRarityBgColor(rarity: AchievementRarity): string {
  switch (rarity) {
    case 'common':
      return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    case 'uncommon':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'rare':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'epic':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'legendary':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'mythic':
      return 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400';
    default:
      return 'bg-slate-200 text-slate-700';
  }
}

export default AchievementsPage;